import { useMemo, useState } from "react";
import type { PreviewContainer } from "../../lib/api";
import { Th, Td, Pagination, EmptySection } from "./ui";

export function ContainersTab({ containers }: { containers: PreviewContainer[] }) {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 100;

  const urnToName = useMemo(() => {
    const m: Record<string, string> = {};
    containers.forEach((c) => { m[c.urn] = c.name; });
    return m;
  }, [containers]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return containers;
    return containers.filter(
      (c) => c.name.toLowerCase().includes(q) || c.subType?.toLowerCase().includes(q) || c.platform.toLowerCase().includes(q),
    );
  }, [containers, search]);

  const pageCount = Math.ceil(filtered.length / PAGE_SIZE);
  const pageData = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  if (containers.length === 0) return <EmptySection label="No containers were emitted by this source." />;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <input
          type="search"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          placeholder="Search containers…"
          className="input flex-1 max-w-sm"
        />
        <span className="text-[12px] text-ash">
          {filtered.length.toLocaleString()} of {containers.length.toLocaleString()}
        </span>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="border-b border-rule bg-paper text-left">
            <tr>
              <Th>Name</Th>
              <Th className="w-32">Platform</Th>
              <Th className="w-28">Type</Th>
              <Th className="w-40">Parent</Th>
            </tr>
          </thead>
          <tbody className="divide-y divide-rule">
            {pageData.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-5 py-8 text-center text-[13px] text-ash">
                  No containers match &ldquo;{search}&rdquo;
                </td>
              </tr>
            ) : (
              pageData.map((c) => {
                const parentName = c.parent ? (urnToName[c.parent] ?? null) : null;
                return (
                  <tr key={c.urn} className="hover:bg-paper transition-colors">
                    <Td>
                      <span className="font-medium text-ink">{c.name || "—"}</span>
                    </Td>
                    <Td><span className="font-mono text-[11.5px] text-ink">{c.platform}</span></Td>
                    <Td>{c.subType ? <span className="chip-neutral text-[11px]">{c.subType}</span> : <span className="text-ash">—</span>}</Td>
                    <Td>
                      {parentName ? (
                        <span className="text-[12px] text-ink">{parentName}</span>
                      ) : c.parent ? (
                        <span className="font-mono text-[10.5px] text-ash">root</span>
                      ) : (
                        <span className="text-ash">—</span>
                      )}
                    </Td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {pageCount > 1 && <Pagination page={page} pageCount={pageCount} onChange={setPage} />}
    </div>
  );
}
